import { Component, Input, OnInit, OnDestroy, OnChanges, Output, EventEmitter, ChangeDetectorRef, SimpleChanges, ChangeDetectionStrategy } from "@angular/core";
import { Router } from "@angular/router";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { AlertService } from "src/app/_core/_services/alert.service";
import { CampaignsService } from "src/app/_core/_services/campaigns/campaigns.service";
import { PromptCloneService } from "src/app/_core/_services/prompt-clone/prompt-clone.service";
import { WorkspacesService } from "src/app/_core/_services/workspaces/workspaces.service";
import { AuthService } from "src/app/modules/auth/services/auth.service";
import { EditCampaignPlannerScheduleModalComponent } from "src/app/modules/profile/campaigns/edit-campaign-planner-schedule/edit-campaign-planner-schedule-modal/edit-campaign-planner-schedule-modal.component";
import { ScheduleCampaignModalComponent } from "src/app/modules/profile/campaigns/edit-campaign-schedule/schedule-campaign-modal/schedule-campaign-modal.component";
import { EditPromptModalComponent } from "src/app/modules/profile/campaigns/edit-prompt-modal/edit-prompt-modal/edit-prompt-modal.component";
import { EditStatusContactModalComponent } from "src/app/modules/profile/edit-status-contact-modal/edit-status-contact-modal.component";

interface Campaign {
  id: number;
  name: string;
  name_workspace: string;
  owner: number;
  status: string;
  is_activated: boolean;
  public: string;
  module?: string;
  engine: number;
  type: number;
  sharing: number;
  shared: number;
  online: number;
  phonenumber_in: number;
  phonenumber_out: number;
  isFavourite: boolean;
}

interface Workspace {
  id: number;
  name: string;
  created: string;
  is_activated?: boolean; // Optional, based on your response
  status?: string;        // Optional, based on your response
  owner: number;          // Ensure this property matches your backend response
  GitLab_id: number;
  active: number;
  public: string;
  resources_id: number;
}


interface ModifiedFields {
  [key: string]: boolean;
}

@Component({
  selector: "app-tables-widget13",
  templateUrl: "./tables-widget13.component.html",
  styleUrls: ["./tables-widget13.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})


export class TablesWidget13Component implements OnInit, OnDestroy, OnChanges {
  @Input() campaigns: Campaign[] = [];
  @Output() editCampaignEvent = new EventEmitter<{
    campaign: Campaign;
    event: Event;
  }>();
  @Output() deleteCampaignEvent = new EventEmitter<number>();
  @Output() switchChangeEvent = new EventEmitter<{
    campaign: Campaign;
    isActivated: boolean;
  }>();
  @Output() viewCampaignEvent = new EventEmitter<{
    id: number;
    public: string;
    name: string;
    module: string;
  }>();
  @Output() openVerticalComponentEvent = new EventEmitter<void>();
  @Output() campaignsFiltered = new EventEmitter<Campaign[]>()
  @Output() workspaceChanged = new EventEmitter<number>();

  @Input() modifiedFields: { [key: string]: boolean } = {};

  ApiUserId: number | null = null;
  fetchedFavouriteIds: number[] = [];

  sortBy = '';
  sortOrder = 'asc';
  searchTerm = '';
  sortedCampaigns: Campaign[] = [];

  private modalRef: NgbModalRef | null = null;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private modalService: NgbModal,
    public  authService: AuthService,
    private workspacesService: WorkspacesService,
    private promptCloneService: PromptCloneService,
    private translate: TranslateService,
    private router: Router,
    private alertService: AlertService,
    private campaignsService: CampaignsService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    console.log('TablesWidget13Component initialized');
    this.ApiUserId = this.authService.getApiUserIdFromAuthService();
    this.markFavourites();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.campaigns) {
      console.log('Campaigns input changed:', changes.campaigns.currentValue);
      this.applySorting();
      this.markFavourites();
    }
  }


  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    console.log('TablesWidget13Component destroyed');
  }

  trackByCampaign(index: number, campaign: Campaign): number {
    return campaign.id;
  }

  onSort(column: string): void {
    if (this.sortBy === column) {
      if (this.sortOrder === 'asc') {
        this.sortOrder = 'desc';
      } else if (this.sortOrder === 'desc') {
        this.sortOrder = '';
        this.sortBy = '';
      } else {
        this.sortOrder = 'asc';
      }
    } else {
      this.sortBy = column;
      this.sortOrder = 'asc';
    }
    this.applySorting();
  }

  getSortIcon(column: string): string {
    if (this.sortBy === column) {
      if (this.sortOrder === 'asc') return 'fa fa-sort-up ms-2 fs-9';
      if (this.sortOrder === 'desc') return 'fa fa-sort-down ms-2 fs-9';
    }
    return 'fa fa-sort ms-2 fs-9';
  }

  onSearch(term: string): void {
    this.searchTerm = (term || '').trim().toLowerCase();
    this.applySorting();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.applySorting();
  }

  private filterBySearch(list: Campaign[]): Campaign[] {
    if (!this.searchTerm) {
      return list;
    }
    const q = this.searchTerm;
    return list.filter((c) => {
      const fields = [
        c.id != null ? String(c.id) : '',
        c.name || '',
        c.phonenumber_in != null ? String(c.phonenumber_in) : '',
        c.phonenumber_out != null ? String(c.phonenumber_out) : '',
      ];
      return fields.some((f) => f.toLowerCase().includes(q));
    });
  }

  private applySorting(): void {
    const filtered = this.filterBySearch(this.campaigns);
    if (!this.sortBy || !this.sortOrder) {
      this.sortedCampaigns = [...filtered];
      this.cdr.detectChanges();
      return;
    }
    this.sortedCampaigns = [...filtered].sort((a, b) => {
      const aVal = (a as any)[this.sortBy];
      const bVal = (b as any)[this.sortBy];
      if (aVal < bVal) return this.sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return this.sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    this.cdr.detectChanges();
  }
  

  private markFavourites(): void {
    if (this.ApiUserId) {
      const favSub = this.campaignsService.getFavourites(this.ApiUserId).subscribe(
        (favouriteIds: number[]) => {
          // Mark campaigns as favorites based on the fetched favorite IDs
          this.campaigns.forEach((campaign) => {
            campaign.isFavourite = favouriteIds.includes(campaign.id);
          });
  
          // Store the favorites in a local variable for tracking
          this.fetchedFavouriteIds = favouriteIds;
          this.cdr.detectChanges(); // Ensure UI updates
        },
        (error) => {
          console.error('Failed to fetch favourites:', error);
        }
      );
      this.subscriptions.add(favSub);
    }
  }
  
  toggleFavourite(campaign: Campaign): void {
    if (this.ApiUserId === null) {
      console.error('ApiUserId is null, cannot toggle favourites.');
      return;
    }
  
    // Toggle favourite status locally
    campaign.isFavourite = !campaign.isFavourite;
  
    // Update the local favorite IDs based on the toggled value
    const updatedFavouriteIds = campaign.isFavourite
      ? [...this.fetchedFavouriteIds, campaign.id] // Add to favorites
      : this.fetchedFavouriteIds.filter((id) => id !== campaign.id); // Remove from favorites
  
    // Immediately trigger change detection to update the UI
    this.cdr.detectChanges();
  
    // Call the API to save the updated favourites
    const favSub = this.campaignsService.saveFavourites(this.ApiUserId, updatedFavouriteIds).subscribe(
      () => {
        console.log(`Favourites updated successfully: ${updatedFavouriteIds}`);
        this.fetchedFavouriteIds = updatedFavouriteIds;
  

      //  this.reloadCampaigns();
      },
      (error) => {
        console.error('Failed to update favourites:', error);
  
        // Revert the change in case of an error
        campaign.isFavourite = !campaign.isFavourite;
        this.cdr.detectChanges(); // Update UI after reverting
      }
    );
  
    // Add the subscription to the cleanup list
    this.subscriptions.add(favSub);
  }
  

  editCampaign(campaign: Campaign, event: Event): void {
    event.preventDefault();
    this.editCampaignEvent.emit({ campaign, event });
  }

  deleteCampaign(campaignId: number, event: Event): void {
    event.preventDefault();
    this.deleteCampaignEvent.emit(campaignId);
  }

  toggleStatus(campaign: Campaign): void {
    const isActivated = !campaign.is_activated;
    campaign.is_activated = isActivated;
    campaign.status = isActivated ? "Aktywna" : "Nieaktywna";
    this.switchChangeEvent.emit({ campaign, isActivated });
  }

  viewCampaign(campaign: Campaign, event: Event): void {
    console.log('campaign ',campaign)
    event.preventDefault();
    this.viewCampaignEvent.emit({
      public: campaign.public,
      name: campaign.name,
      id: campaign.id,
      module: campaign.module || "",
    });
  }

  viewWorkspaceCampaigns(workspaceName: string): void {
    this.workspacesService.getWorkspaces().subscribe(
      (response) => {
        const workspaces = response.data;
        const workspace = workspaces.find((ws) => ws.name === workspaceName);
  
        if (workspace) {
          const workspaceId = workspace.id;
          const userOwnsWorkspace = workspace.owner === this.ApiUserId;
  
          if (userOwnsWorkspace) {
            this.workspaceChanged.emit(workspaceId); // Emit workspaceId to parent
            this.router.navigate([`/crafted/pages/profile/campaigns`, workspaceId]);
          } else {
            this.translate.get('WORKSPACE.CAMPAIGN_SHARED_ONLY').subscribe((res: string) => {
              this.alertService.info(res);
            });
          }
        } else {
          this.translate.get('WORKSPACE.CAMPAIGN_SHARED_ONLY').subscribe((res: string) => {
            this.alertService.info(res);
          });
        }
      },
      (error) => {
        console.error("Error fetching workspaces:", error);
      }
    );
  }

  
  openVerticalComponent(event: Event): void {
    event.preventDefault();
    this.openVerticalComponentEvent.emit();
  }

  openCreateAppModal(campaign: Campaign, event: Event): void {
    event.preventDefault();
    this.openModalWithCampaignData(campaign);
  }

  openScheduleModal(publicCampaign: string): void {
    // Fetch Dialer Schedule using the service
    this.campaignsService.getDialerSchedule(publicCampaign).subscribe({
      next: (scheduleData) => {
        const modalRef = this.modalService.open(ScheduleCampaignModalComponent, {
          centered: true,
          size: 'lg',
        });
        // Pass the fetched schedule data to the modal
        modalRef.componentInstance.scheduleData = {
          data: scheduleData,
          message: 'Successfully retrieved dialer schedule',
        };
      },
      error: (error) => {
        console.error('Error fetching dialer schedule:', error);
        this.translate.get('ERRORS.FAILED_FETCH_DIALER_SCHEDULE').subscribe((res: string) => {
          this.alertService.error(res);
      });
      
      },
    });
  }
  
  openScheduleStepsModal(publicCampaign: string): void {
    // Fetch Planner Schedule using the service
    this.campaignsService.getPlannerSchedule(publicCampaign).subscribe({
      next: (scheduleData) => {
        const modalRef = this.modalService.open(EditCampaignPlannerScheduleModalComponent, {
          centered: true,
          size: 'lg',
        });
        // Pass the fetched schedule data to the modal
        modalRef.componentInstance.scheduleData = {
          data: scheduleData,
          message: 'Successfully retrieved planner schedule',
        };
      },
      error: (error) => {
        console.error('Error fetching planner schedule:', error);
        this.translate.get('ERRORS.FAILED_FETCH_PLANNER_SCHEDULE').subscribe((res: string) => {
          this.alertService.error(res);
      });
      
      },
    });
  }
  
  
  private openModalWithCampaignData(campaign: Campaign, modifiedFields?: ModifiedFields): void {
    this.modalRef = this.modalService.open(EditPromptModalComponent, { centered: true, size: 'xl' });
    this.modalRef.componentInstance.publicCampaign = campaign.public;

    // Pass cloned prompt state to the modal
    const clonedPromptData = this.promptCloneService.getClonedPromptData();
    this.modalRef.componentInstance.clonedPromptAvailable = !!clonedPromptData;

    if (modifiedFields) {
        this.modalRef.componentInstance.modifiedFields = modifiedFields;
        this.modalRef.componentInstance.isReopened = true; // Set isReopened to true when reopening
    }

    // Handle save and reopen logic explicitly triggered on save
    this.modalRef.componentInstance.saveAndReopen.subscribe((modifiedFieldsFromModal: ModifiedFields) => {
        // Only close and reopen on explicit save
        this.modalRef?.close();
        this.openModalWithCampaignData(campaign, modifiedFieldsFromModal);
    });

    // Handle modal close/dismiss events
    this.modalRef.result.then(
        (result) => {
            console.log('Modal closed with result:', result);
        },
        (reason) => {
            if (reason !== 'Cross click') { // Avoid unnecessary log for standard close
                console.log('Modal dismissed:', reason);
            }
        }
    );
}
goToCampaignReport(campaign: Campaign): void {
  this.router.navigate(['/pages/campaign-report'], {
    queryParams: { 
      uuid: campaign.public,
      name: campaign.name
     }
  });
}

  
}
